export type ChartsAgingBucket = {
  name: string;
  value: number;
  color: string;
};

export type ReceivablesChartsSummaryInput = {
  agingBuckets: ChartsAgingBucket[];
  unscopedAgingBuckets?: ChartsAgingBucket[];
  unscopedOpenBalance?: number;
  unscopedCriticalAmount?: number;
  unscopedCriticalInvoiceCount?: number;
  criticalAmount: number;
  criticalInvoicesCount: number;
};

export type ReceivablesChartsMetrics = {
  agingBuckets: ChartsAgingBucket[];
  openBalanceAmount: number;
  criticalAmount: number;
  criticalInvoicesCount: number;
};

const NINETY_PLUS_BUCKET = "90+ days";

export function sumAgingBucketValues(buckets: ChartsAgingBucket[]): number {
  return buckets.reduce((sum, bucket) => sum + bucket.value, 0);
}

export function agingBucketAmount(
  buckets: ChartsAgingBucket[],
  name: string,
): number {
  return buckets.find((bucket) => bucket.name === name)?.value ?? 0;
}

/** KPI card values must use the same aging buckets shown in the pie chart. */
export function resolveReceivablesChartsMetrics(
  summary: ReceivablesChartsSummaryInput | null | undefined,
): ReceivablesChartsMetrics {
  const agingBuckets =
    summary?.unscopedAgingBuckets ?? summary?.agingBuckets ?? [];
  const openBalanceAmount =
    summary?.unscopedOpenBalance ?? sumAgingBucketValues(agingBuckets);
  const ninetyPlusFromBuckets = agingBuckets.find(
    (bucket) => bucket.name === NINETY_PLUS_BUCKET,
  )?.value;
  const criticalAmount =
    summary?.unscopedCriticalAmount ??
    ninetyPlusFromBuckets ??
    summary?.criticalAmount ??
    0;
  const criticalInvoicesCount =
    summary?.unscopedCriticalInvoiceCount ??
    summary?.criticalInvoicesCount ??
    0;

  return {
    agingBuckets,
    openBalanceAmount,
    criticalAmount,
    criticalInvoicesCount,
  };
}

export function chartsMetricsMatchAgingBuckets(
  metrics: ReceivablesChartsMetrics,
): boolean {
  const bucketTotal = sumAgingBucketValues(metrics.agingBuckets);
  const ninetyPlus = agingBucketAmount(metrics.agingBuckets, NINETY_PLUS_BUCKET);
  return (
    metrics.openBalanceAmount === bucketTotal &&
    metrics.criticalAmount === ninetyPlus
  );
}
